import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import axios from '../utils/axios';

const OffersManagement = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [viewOffer, setViewOffer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    validUntil: '',
    discountType: 'percentage',
    discountValue: 0,
    maxRedemptions: '',
    status: 'Active'
  });

  // Fetch offers
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/offers', {
        params: { search: searchQuery }
      });
      
      if (response.data.success) {
        setOffers(response.data.data);
      }
    } catch (error) {
      showSnackbar('Error fetching offers', 'error');
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [searchQuery]);

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle view dialog
  const handleOpenViewDialog = (offer) => {
    setViewOffer(offer);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewOffer(null);
  };

  // Handle dialog open
  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setEditMode(true);
      setSelectedOffer(offer);
      setFormData({
        title: offer.title,
        description: offer.description || '',
        validUntil: offer.validUntil.split('T')[0],
        discountType: offer.discountType,
        discountValue: offer.discountValue,
        maxRedemptions: offer.maxRedemptions || '',
        status: offer.status
      });
    } else {
      setEditMode(false);
      setSelectedOffer(null);
      setFormData({
        title: '',
        description: '',
        validUntil: '',
        discountType: 'percentage',
        discountValue: 0,
        maxRedemptions: '',
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedOffer(null);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (editMode && selectedOffer) {
        const response = await axios.put(`/offers/${selectedOffer._id}`, formData);
        if (response.data.success) {
          showSnackbar('Offer updated successfully');
          fetchOffers();
        }
      } else {
        const response = await axios.post('/offers', formData);
        if (response.data.success) {
          showSnackbar('Offer created successfully');
          fetchOffers();
        }
      }
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving offer', 'error');
      console.error('Error saving offer:', error);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        const response = await axios.delete(`/offers/${id}`);
        if (response.data.success) {
          showSnackbar('Offer deleted successfully');
          fetchOffers();
        }
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Error deleting offer', 'error');
        console.error('Error deleting offer:', error);
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'default';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Offers Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage promotional offers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ textTransform: 'none' }}
        >
          Create Offer
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ width: 300 }}
        />
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : offers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No offers found. Create your first offer!
          </Typography>
        </Box>
      ) : (
        /* Offers List */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {offers.map((offer) => (
            <Card key={offer._id} sx={{ boxShadow: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        bgcolor: '#FFF3E0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <OfferIcon sx={{ color: '#F57C00' }} />
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {offer.title}
                        </Typography>
                        <Chip
                          label={offer.status}
                          size="small"
                          color={getStatusColor(offer.status)}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Valid until {new Date(offer.validUntil).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                      {offer.description && (
                        <Typography variant="body2" color="text.secondary">
                          {offer.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenViewDialog(offer)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(offer._id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Total Redemptions */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Redemptions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {offer.totalRedemptions}
                  </Typography>
                </Box>

                {/* Edit Button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleOpenDialog(offer)}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Edit Offer
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OfferIcon sx={{ color: '#F57C00' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Offer Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewOffer && (
            <Box sx={{ pt: 1 }}>
              {/* Title and Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Title
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {viewOffer.title}
                  </Typography>
                  <Chip
                    label={viewOffer.status}
                    size="small"
                    color={getStatusColor(viewOffer.status)}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              {viewOffer.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {viewOffer.description}
                  </Typography>
                </Box>
              )}

              {/* Discount Information */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Discount Type
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {viewOffer.discountType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Discount Value
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {viewOffer.discountType === 'percentage' ? `${viewOffer.discountValue}%` : `$${viewOffer.discountValue}`}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Validity and Redemptions */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Valid Until
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(viewOffer.validUntil).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Max Redemptions
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {viewOffer.maxRedemptions || 'Unlimited'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Total Redemptions */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Total Redemptions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {viewOffer.totalRedemptions}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseViewDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button
            onClick={() => {
              handleCloseViewDialog();
              handleOpenDialog(viewOffer);
            }}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Edit Offer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Offer' : 'Create New Offer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Type"
                name="discountType"
                value={formData.discountType}
                onChange={handleInputChange}
                select
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed Amount</MenuItem>
                <MenuItem value="free">Free</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Value"
                name="discountValue"
                type="number"
                value={formData.discountValue}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valid Until"
                name="validUntil"
                type="date"
                value={formData.validUntil}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Redemptions"
                name="maxRedemptions"
                type="number"
                value={formData.maxRedemptions}
                onChange={handleInputChange}
                placeholder="Leave empty for unlimited"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                select
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OffersManagement;